/**
 * Hero header — entrata dei testi con slide-up mascherato riga per riga.
 *
 * Ogni riga viene racchiusa in un contenitore che la ritaglia (overflow: clip) e
 * risale da sotto: l'effetto è quello di un testo che emerge da dietro una
 * fessura, non di un blocco che scivola. Riferimento: gsap-text-animator.
 *
 * NON è agganciata allo scroll: l'header è il primo elemento della pagina,
 * quindi parte al caricamento.
 *
 * Aggancio: [data-header-text] — il partial template-parts/global/page-header.php
 * e gli usi inline che ne riusano le classi (archive-realizzazioni.php,
 * page-template/aboutus.php), più la hero di page-template/esg.php.
 * Anima i figli diretti.
 *
 * [data-header-text-whole] su un figlio lo esclude dallo split: entra come
 * oggetto unico, con una risalita più corta e senza maschera. Serve per gli
 * elementi che non sono testo scorrevole — i bottoni su tutti: .btn-solid è
 * inline-flex con gap, e i wrapper di riga (display: block) ne romperebbero il
 * layout. A fine tween si fa clearProps sul transform, altrimenti il transform
 * inline lasciato da GSAP bloccherebbe quello dell'hover.
 *
 * Scelte tecniche:
 *
 *  - SplitText di gsap 3.15 al posto di split-type: è già nel pacchetto, quindi
 *    nessuna dipendenza nuova e nessuno <script> da CDN.
 *
 *  - mask: 'lines' costruisce nativamente i wrapper che ritagliano le righe.
 *    Senza maschera lo slide-up mostrerebbe il testo che arriva da fuori box.
 *
 *  - Lo split a righe dipende da dove va a capo il testo, quindi va rifatto
 *    quando cambia la larghezza o quando i webfont sostituiscono il fallback.
 *    autoSplit: true se ne occupa (ascolta document.fonts loadingdone e i
 *    resize); l'entrata però resta una tantum — vedi il flag `played`.
 *
 *  - Si aspetta document.fonts.ready prima di splittare: misurare le righe con
 *    il font di fallback e poi rifare tutto darebbe uno scatto visibile proprio
 *    sull'elemento più in vista della pagina.
 *
 * Lo stato iniziale nascosto è in CSS (_reveal.scss, sotto .js-reveal): le righe
 * non esistono finché il JS non splitta, quindi si nasconde il blocco intero e
 * lo si riaccende con .is-split quando le righe sono già in posizione di
 * partenza dentro le maschere.
 */
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';

( function () {
	'use strict';

	const blocks = document.querySelectorAll( '[data-header-text]' );
	if ( ! blocks.length ) {
		return;
	}

	if ( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		blocks.forEach( ( block ) => block.classList.add( 'is-split' ) ); // testo visibile, nessuno split.
		return;
	}

	gsap.registerPlugin( SplitText );

	// dots-pattern.js tiene in cache le maschere attorno al testo: vanno
	// ricostruite quando le righe si fermano nella posizione definitiva.
	const settled = () => window.dispatchEvent( new CustomEvent( 'am:textsettled' ) );

	// Entrata degli elementi non splittati (bottoni e simili): stessa curva delle
	// righe, risalita più corta perché senza maschera non c'è nulla che ritagli.
	const wholeVars = {
		y: 24,
		opacity: 0,
		duration: 0.5,
		ease: 'circ.out',
		stagger: 0.1,
		clearProps: 'transform',
	};

	const animate = ( block ) => {
		const kids = Array.prototype.slice.call( block.children );
		if ( ! kids.length ) {
			return;
		}

		const wholes = kids.filter( ( k ) => k.hasAttribute( 'data-header-text-whole' ) );
		const splittable = kids.filter( ( k ) => ! k.hasAttribute( 'data-header-text-whole' ) );

		// Nessun testo da splittare: restano solo gli oggetti interi.
		if ( ! splittable.length ) {
			gsap.from( wholes, wholeVars );
			block.classList.add( 'is-split' );
			settled();
			return;
		}

		let played = false;

		SplitText.create( splittable, {
			type: 'lines',
			mask: 'lines',
			tag: 'span',
			linesClass: 'am-line',
			autoSplit: true,
			onSplit: ( self ) => {
				if ( played ) {
					// Re-split dopo un resize: le righe restano dove sono.
					settled();
					return;
				}
				played = true;

				// immediateRender di gsap.from mette subito le righe sotto la
				// maschera, quindi .is-split può riaccendere il blocco senza
				// che nulla trapeli.
				const tl = gsap.timeline( { onComplete: settled } );

				tl.from( self.lines, {
					yPercent: 100,
					opacity: 0,
					duration: 0.5,
					ease: 'circ.out',
					stagger: 0.1,
				} );

				// Gli oggetti interi chiudono la sequenza, sovrapposti alle
				// ultime righe così la cascata non si spezza.
				if ( wholes.length ) {
					tl.from( wholes, wholeVars, '>-0.3' );
				}

				block.classList.add( 'is-split' );

				return tl;
			},
		} );
	};

	const start = () => blocks.forEach( animate );

	if ( document.fonts && document.fonts.ready ) {
		// Il timeout evita che un webfont che non arriva blocchi l'entrata.
		Promise.race( [
			document.fonts.ready,
			new Promise( ( resolve ) => window.setTimeout( resolve, 1500 ) ),
		] ).then( start );
	} else {
		start();
	}
}() );
